import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useDataAuth } from '../../hooks/useDataAuth';
import FormHelperText from '@mui/material/FormHelperText';
import ContactsIcon from '@mui/icons-material/Contacts';
import FormControl from '@mui/material/FormControl';
import IconButton from "@mui/material/IconButton";
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';

import taxResponsibilities from "../../data/taxResponsibilities.json";
import identificationTypes from "../../data/identificationTypes.json";
import { MultipleSelectAutoComplete } from '../others/MultipleSelectAutoComplete';
import { getContactsConcat } from "../../services/meetings.service";
import { fetchServiceJson } from "../../services/service.service";
import { fetchClientByNit } from '../../services/client.service';
import { MultipleSelect } from '../others/MultipleSelect';
import { calculateDV } from '../../utils/invoice';
import { CreateContact } from './dialogs/CreateContact';

import '../../styles/components/_forms.scss';
import { useTranslation } from 'react-i18next';
import { toArray } from '../../utils/arrayUtils';

export const CreateFormClient = forwardRef(({ dto, handleInputChange, handleDirectInputChange, isView }, ref) => {
    const { t } = useTranslation();
    const [{ userSession }] = useDataAuth();
    const [sendNotification] = useNotification();
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [categories, setcategories] = useState([]);
    const [segments, setsegment] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [openDialogContact, setOpenDialogCompany] = useState(false);

    const handleSelectObligation = (event) => {
        const { target: { value } } = event;
        handleDirectInputChange('supplier_tax_level_code', value.join(','));
    };

    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    useImperativeHandle(ref, () => ({
        validateForm,
    }));

    const requiredField = ['type_identification', 'business_name', 'identification', 'additional_account_id'];
    const validateForm = () => {
        let hasError = false;
        requiredField.forEach((field) => {
            const isValid = validateField(field, dto[field]);
            if (!isValid) hasError = true;
        });
        return !hasError;
    };

    const calculate = () => {
        const dv = calculateDV(dto.identification);
        handleDirectInputChange("identification_digit", dv);
    }

    const handleClient = async ({ target }) => {
        const identification = target.value;
        handleDirectInputChange("identification", identification);
        if (identification.length > 7) {
            setTimeout(async () => {
                const response = await fetchClientByNit(userSession.token, identification);
                if (response.data) {
                    handleDirectInputChange("identification", "");
                    handleDirectInputChange("identification_digit", "");
                    sendNotification({
                        msg: t("client.alreadyExists"),
                        variant: "info",
                    });
                }
            }, 1000);
        }
    };

    const handleOpenDialogContact = (saved) => {
        if (saved && saved.id_contact) {
            if (saved.id_contact === "EXIST") {
                sendNotification({
                    msg: t("client.alreadyExists"),
                    variant: "info",
                });
            } else {
                const searchContacts = async () => {
                    const response = await getContactsConcat(userSession.token);
                    if (response.data && response.data.length > 0) {
                        const mappedContacts = response.data.map((item) => ({
                            key: item.name,
                            value: item.code.toString()
                        }));
                        setContacts(mappedContacts);
                        handleDirectInputChange('contacts', [...dto.contacts, saved.id_contact.toString()]);
                    }
                }
                searchContacts()
                sendNotification({ variant: 'success', msg: t("messages.saveSuccess") });
            }
        }
        setOpenDialogCompany(!openDialogContact);
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchServiceJson('MENU-CLIENT', userSession.token);
                const contactResponse = await getContactsConcat(userSession.token);
                if (contactResponse.data && contactResponse.data.length > 0) {
                    const mappedContacts = contactResponse.data.map((item) => ({
                        key: item.name,
                        value: item.code.toString()
                    }));
                    setContacts(mappedContacts);
                }
                if (response.data && response.data.length > 0) {
                    const labels = response.data;
                    labels.forEach((item) => {
                        const parsedValue = JSON.parse(item.value);
                        if (item.field === "category") {
                            setcategories(parsedValue);
                        }
                        else if (item.field === "segment") {
                            setsegment(parsedValue);
                        }
                    });
                }
            } catch (e) {
                console.error("Error fetching contact JSON:", e.message);
            } finally {
                console.log("Finaliza la carga de datos");
            }
        };
        fetchData();
    }, [userSession.token]);

    return (
        <>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h6">
                        {t("client.clientInfo")}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.type_identification?.isError)}
                    >
                        <InputLabel>Tipo de Identificación</InputLabel>
                        <Select name="type_identification" label="Tipo de Identificación"
                            value={dto.type_identification}
                            onChange={handleInputChange}
                            onBlur={calculate}
                        >
                            {identificationTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.type_identification?.isError && (
                            <FormHelperText>{errorInputs.type_identification.messageError}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required name='identification' label="Identificación" size="small" type="number" className='input-no-spinner'
                        value={dto.identification}
                        error={Boolean(errorInputs.identification?.isError)}
                        helperText={errorInputs.identification?.messageError || ''}
                        onChange={handleClient}
                        onBlur={calculate}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth disabled name='identification_digit' label="Dígito Verificación" size="small" type="number" className='input-no-spinner'
                        value={dto.identification_digit}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required name='business_name' label="Razón Social (Nombre Usuario)" size="small"
                        value={dto.business_name}
                        error={Boolean(errorInputs.business_name?.isError)}
                        helperText={errorInputs.business_name?.messageError || ''}
                        onChange={handleInputChange}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth size="small" name='last_name' label="Segundo Nombre"
                        value={dto.last_name}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.last_name?.isError)}
                        helperText={errorInputs.last_name?.messageError || ''}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small"
                    >
                        <InputLabel>Tipo de Tributo</InputLabel>
                        <Select
                            label="Tipo de Tributo"
                            name="supplier_tax_scheme"
                            value={dto.supplier_tax_scheme}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="01">IVA</MenuItem>
                            <MenuItem value="04">INC</MenuItem>
                            <MenuItem value="ZA">IVA e INC</MenuItem>
                            <MenuItem value="ZZ">No aplica</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={!!errorInputs.additional_account_id?.isError}
                    >
                        <InputLabel>Supplier Additional Account ID</InputLabel>
                        <Select label={t("commonFields.regimeType")} name="additional_account_id"
                            value={dto.additional_account_id}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="1">Jurídico</MenuItem>
                            <MenuItem value="2">Natural</MenuItem>
                        </Select>
                        {errorInputs.additional_account_id?.isError && <FormHelperText>{errorInputs.additional_account_id.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        name='category'
                        label="Categoria"
                        select
                        fullWidth
                        variant="outlined"
                        value={dto.category}
                        onChange={handleInputChange}
                        size="small"
                        defaultValue="none"
                        error={Boolean(errorInputs.category?.isError)}
                        helperText={errorInputs.category?.messageError || ''}
                    >
                        {categories.map((item) => (
                            <MenuItem
                                key={item.name}
                                value={item.code}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        size="small"
                        name='segment'
                        label="Segmento"
                        value={dto.segment}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.segment?.isError)}
                        helperText={errorInputs.segment?.messageError || ''}
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflowY: "auto",
                                    },
                                },
                            },
                        }}
                    >
                        {segments.map((item) => (
                            <MenuItem
                                key={item.name}
                                value={item.code}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <MultipleSelect
                        data={taxResponsibilities}
                        values={toArray(dto.supplier_tax_level_code)}
                        handleChange={handleSelectObligation}
                        label={'Obligaciones'}
                    />
                </Grid>


                <Grid size={{ xs: 12, md: 7 }}>
                    <MultipleSelectAutoComplete
                        label={'Contactos'}
                        data={contacts}
                        values={dto.contacts}
                        handleChange={({ target }) => handleDirectInputChange('contacts', target.value)}
                        name={'contacts'}
                        error={errorInputs.contacts?.isError}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-start', padding: 0 }}>
                    <Tooltip title={t("add")} placement="top" arrow>
                        <IconButton
                            aria-label="add"
                            onClick={handleOpenDialogContact}
                            sx={{
                                color: isView ? 'gray' : '#6728AF',
                                padding: 0
                            }}
                            disabled={isView}
                        >
                            <ContactsIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <CreateContact data={dto} open={openDialogContact} onClose={handleOpenDialogContact} />
            </Grid>
        </>
    )
});